import {
  getSpaPackageConfigGroups,
  getSpaPackageConfigGroupsKey,
} from '@features/spa/hooks/useGetSpaPackageConfigGroups';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import { canGuestsShareTimeslot, getOptionTimeslots } from '../helpers/timeslotCapacity';

const CONFIG_GROUPS_CACHE_TIME = 60000;

interface UseMultiGuestTimeslotAvailabilityOptions {
  dates: (string | undefined)[];
  numberOfGuests: number;
  propertyId?: number;
  spaPackageId?: number;
  spaOpenTime?: string;
  spaCloseTime?: string;
}

interface UseMultiGuestTimeslotAvailabilityReturn {
  canGuestsShareTimeslotOn: (date: string, optionIds: number[]) => boolean | undefined;
}

export const useMultiGuestTimeslotAvailability = ({
  dates,
  numberOfGuests,
  propertyId,
  spaPackageId,
  spaOpenTime,
  spaCloseTime,
}: UseMultiGuestTimeslotAvailabilityOptions): UseMultiGuestTimeslotAvailabilityReturn => {
  const isEnabled = numberOfGuests > 1 && !!propertyId && !!spaPackageId && !!spaOpenTime;

  const datesKey = (Array.isArray(dates) ? dates : []).filter(Boolean).join(',');

  const uniqueDates = useMemo(
    () => (isEnabled && datesKey ? Array.from(new Set(datesKey.split(','))) : []),
    [datesKey, isEnabled]
  );

  const results = useQueries({
    queries: uniqueDates.map((date) => ({
      queryKey: getSpaPackageConfigGroupsKey(
        Number(propertyId),
        Number(spaPackageId),
        date,
        numberOfGuests,
        spaOpenTime,
        spaCloseTime
      ),
      queryFn: () =>
        getSpaPackageConfigGroups(
          Number(propertyId),
          Number(spaPackageId),
          date,
          numberOfGuests,
          spaOpenTime,
          spaCloseTime
        ),
      staleTime: CONFIG_GROUPS_CACHE_TIME,
      cacheTime: CONFIG_GROUPS_CACHE_TIME,
      retry: false,
    })),
  });

  const canGuestsShareTimeslotOn = (date: string, optionIds: number[]): boolean | undefined => {
    if (numberOfGuests < 2) {
      return false;
    }

    const dateIndex = uniqueDates.indexOf(date);
    const result = dateIndex === -1 ? undefined : results[dateIndex];

    if (!result?.data) {
      return undefined;
    }

    const configurationGroups = result.data as ConfigurationGroupType[];

    return (Array.isArray(optionIds) ? optionIds : [])
      .filter((optionId) => !!optionId && !Number.isNaN(optionId))
      .some((optionId) =>
        canGuestsShareTimeslot(getOptionTimeslots(configurationGroups, optionId), numberOfGuests)
      );
  };

  return { canGuestsShareTimeslotOn };
};
